import { useState } from 'react';
import { cn } from '../../../../../../lib/utils';
import { cardClass, primaryText, secondaryText, semiBoldText, smallTextSize } from '../../../../../../styling/stylingHelper';
import {
  runVangelderPakbonReplay,
  type VangelderReplayResult,
  type VangelderReplayEmail,
  type VangelderReplayPrediction,
} from '../../../../../../services/admin/adminService';
import { ChevronDown, ChevronRight, Truck } from 'lucide-react';
import { TrivecSummaryChip } from './panelPrimitives';
import type { TFn } from '../../types';

/**
 * Van Gelder pakbon replay — the flip-time backfill for `vangelderPakbonMode`.
 *
 * After switching a restaurant's vangelder platform to pakbon mode, rerun the pakbon emails of a
 * window through the normal pipeline so the standing supersede rule converts CS-numbered rows to
 * long-number rows (the numbers Bidfood facturen reference). Dry run predicts per email without
 * writing and costs no AI; Apply replays for real and sits behind a confirm dialog.
 */
export function VangelderPakbonReplayPanel({ t }: { t: TFn }) {
  const [open, setOpen] = useState(false);
  const [idType, setIdType] = useState<'allOn' | 'restaurant' | 'restaurantPlatform'>('allOn');
  const [targetId, setTargetId] = useState('');
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 21);
    return d.toISOString().slice(0, 10);
  });
  const [toDate, setToDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [account, setAccount] = useState<'both' | 'cloud63' | 'cloud64'>('both');
  // Per mailbox. The Gmail listing is subject-only, so it sees EVERY Van Gelder customer's pakbon in
  // the window, not just the targeted ones — a wide window hits the cap long before it runs out of
  // relevant emails, and a truncated list looks exactly like a complete one.
  const [maxEmails, setMaxEmails] = useState(200);
  const [status, setStatus] = useState<'idle' | 'loading' | 'done' | 'error'>('idle');
  const [result, setResult] = useState<VangelderReplayResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const allOn = idType === 'allOn';

  const run = async (dryRun: boolean) => {
    const id = targetId.trim();
    if (!allOn && !id) return;
    if (!dryRun && !window.confirm(t('adminTesting.vangelderReplay.applyConfirm'))) return;
    try {
      setStatus('loading');
      setError(null);
      const r = await runVangelderPakbonReplay({
        ...(allOn
          ? { allPakbonModePlatforms: true }
          : idType === 'restaurant' ? { restaurantId: id } : { restaurantPlatformId: id }),
        fromDate,
        toDate,
        dryRun,
        maxEmails,
        ...(account !== 'both' ? { account } : {}),
      });
      setResult(r);
      setStatus('done');
    } catch (e: any) {
      setError(e.message || 'Failed to run vangelder pakbon replay');
      setStatus('error');
    }
  };

  const inputCls = cn('px-3 py-2 rounded-lg border', 'border-gray-300 dark:border-gray-600', 'bg-white dark:bg-gray-700', primaryText, smallTextSize);
  const loading = status === 'loading';

  return (
    <div className={cn(cardClass, 'px-3 py-4 sm:px-4')}>
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <Truck className="w-4 h-4 shrink-0 text-orange-600 dark:text-orange-400" />
          <h3 className={cn(primaryText, semiBoldText, 'truncate')}>{t('adminTesting.vangelderReplay.title')}</h3>
        </div>
        {open ? <ChevronDown className={cn('w-4 h-4 shrink-0', secondaryText)} /> : <ChevronRight className={cn('w-4 h-4 shrink-0', secondaryText)} />}
      </button>

      {open && (
        <div className="mt-4 space-y-4">
          <p className={cn(secondaryText, 'text-xs')}>{t('adminTesting.vangelderReplay.desc')}</p>

          <div className="flex flex-wrap items-end gap-3">
            <div className="w-56">
              <label className={cn(secondaryText, 'text-xs block mb-1')}>{t('adminTesting.vangelderReplay.idTypeLabel')}</label>
              <select value={idType} onChange={(e) => setIdType(e.target.value as 'allOn' | 'restaurant' | 'restaurantPlatform')} className={cn(inputCls, 'w-full')}>
                <option value="allOn">{t('adminTesting.vangelderReplay.idTypeAllOn')}</option>
                <option value="restaurant">{t('adminTesting.vangelderReplay.idTypeRestaurant')}</option>
                <option value="restaurantPlatform">{t('adminTesting.vangelderReplay.idTypePlatform')}</option>
              </select>
            </div>
            <div className="flex-1 min-w-[240px]">
              <label className={cn(secondaryText, 'text-xs block mb-1')}>{allOn ? t('adminTesting.vangelderReplay.idTypeAllOn') : idType === 'restaurant' ? t('adminTesting.vangelderReplay.idTypeRestaurant') : t('adminTesting.vangelderReplay.idTypePlatform')}</label>
              <input
                type="text"
                value={allOn ? '' : targetId}
                disabled={allOn}
                onChange={(e) => setTargetId(e.target.value)}
                placeholder={allOn ? t('adminTesting.vangelderReplay.allOnPlaceholder') : t('adminTesting.vangelderReplay.idPlaceholder')}
                className={cn(inputCls, 'w-full', allOn && 'opacity-50 cursor-not-allowed')}
              />
            </div>
            <div className="w-40">
              <label className={cn(secondaryText, 'text-xs block mb-1')}>{t('adminTesting.vangelderReplay.fromLabel')}</label>
              <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className={cn(inputCls, 'w-full')} />
            </div>
            <div className="w-40">
              <label className={cn(secondaryText, 'text-xs block mb-1')}>{t('adminTesting.vangelderReplay.toLabel')}</label>
              <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className={cn(inputCls, 'w-full')} />
            </div>
            <div className="w-32">
              <label className={cn(secondaryText, 'text-xs block mb-1')}>{t('adminTesting.vangelderReplay.accountLabel')}</label>
              <select value={account} onChange={(e) => setAccount(e.target.value as 'both' | 'cloud63' | 'cloud64')} className={cn(inputCls, 'w-full')}>
                <option value="both">{t('adminTesting.vangelderReplay.accountBoth')}</option>
                <option value="cloud63">cloud63</option>
                <option value="cloud64">cloud64</option>
              </select>
            </div>
            <div className="w-28">
              <label className={cn(secondaryText, 'text-xs block mb-1')}>{t('adminTesting.vangelderReplay.maxEmailsLabel')}</label>
              <input
                type="number"
                min={1}
                max={500}
                value={maxEmails}
                onChange={(e) => setMaxEmails(Math.max(1, Math.min(500, Number(e.target.value) || 1)))}
                className={cn(inputCls, 'w-full')}
              />
            </div>
            <button
              onClick={() => run(true)}
              disabled={loading || (!allOn && !targetId.trim())}
              className={cn('px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed', primaryText, smallTextSize, semiBoldText)}
            >
              {loading ? t('adminTesting.vangelderReplay.running') : t('adminTesting.vangelderReplay.dryRun')}
            </button>
            <button
              onClick={() => run(false)}
              disabled={loading || (!allOn && !targetId.trim())}
              className={cn('px-4 py-2 rounded-lg bg-orange-600 dark:bg-orange-600 text-white dark:text-white hover:bg-orange-700 dark:hover:bg-orange-700 disabled:opacity-40 disabled:cursor-not-allowed', smallTextSize, semiBoldText)}
            >
              {t('adminTesting.vangelderReplay.apply')}
            </button>
          </div>

          {error && (
            <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm">{error}</div>
          )}

          {result && <VangelderReplayResults result={result} t={t} />}
        </div>
      )}
    </div>
  );
}

/** The labels shared by the summary chips and the per-email prediction badges. */
const VANGELDER_PREDICTION_KEYS: Record<VangelderReplayPrediction, string> = {
  supersede: 'adminTesting.vangelderReplay.countSupersede',
  insert: 'adminTesting.vangelderReplay.countInsert',
  skip: 'adminTesting.vangelderReplay.countSkip',
  unresolvable: 'adminTesting.vangelderReplay.countUnresolvable',
  unknown: 'adminTesting.vangelderReplay.countUnknown',
};

const VANGELDER_SKIP_REASON_KEYS: Record<NonNullable<VangelderReplayEmail['skippedReason']>, string> = {
  no_pakbon_pdf: 'adminTesting.vangelderReplay.countNoPdf',
  no_client_code: 'adminTesting.vangelderReplay.countNoClientCode',
  client_not_matched: 'adminTesting.vangelderReplay.countNotMatched',
};

function VangelderReplayResults({ result, t }: { result: VangelderReplayResult; t: TFn }) {
  const [showEmails, setShowEmails] = useState(false);
  const c = result.counts;
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className={cn('px-2 py-0.5 rounded-full font-semibold',
            result.dryRun
              ? 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
              : 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300')}>
            {result.dryRun ? t('adminTesting.vangelderReplay.modeDry') : t('adminTesting.vangelderReplay.modeApply')}
          </span>
          <span className={secondaryText}>
            {t('adminTesting.vangelderReplay.summary', {
              listed: result.emailsListed,
              matched: result.emailsMatched,
              replayed: result.emailsReplayed,
              targets: result.targetPlatforms.length,
            })}
          </span>
        </div>
        {result.listingTruncated && (
          <div className="p-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 text-xs">
            {t('adminTesting.vangelderReplay.truncated')}
          </div>
        )}
        {/* Which connections the run actually targeted — the whole point of the all-ON sweep is that
            you did not pick them by hand, so they have to be visible. */}
        {result.targetPlatforms.some((p) => p.label) && (
          <div className={cn(secondaryText, 'text-xs')}>
            {result.targetPlatforms.map((p) => p.label ?? p.restaurantPlatformId).join(' · ')}
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          <TrivecSummaryChip label={t('adminTesting.vangelderReplay.countSupersede')} value={c.supersede} />
          <TrivecSummaryChip label={t('adminTesting.vangelderReplay.countInsert')} value={c.insert} />
          <TrivecSummaryChip label={t('adminTesting.vangelderReplay.countSkip')} value={c.skip} />
          <TrivecSummaryChip label={t('adminTesting.vangelderReplay.countUnresolvable')} value={c.unresolvable} />
          <TrivecSummaryChip label={t('adminTesting.vangelderReplay.countUnknown')} value={c.unknown} />
          <TrivecSummaryChip label={t('adminTesting.vangelderReplay.countNotMatched')} value={c.clientNotMatched} />
          {/* Without these two, listed − matched is an unexplained gap: an email dropped here is never
              replayed, in dry run OR apply. */}
          <TrivecSummaryChip label={t('adminTesting.vangelderReplay.countNoPdf')} value={c.noPakbonPdf} />
          <TrivecSummaryChip label={t('adminTesting.vangelderReplay.countNoClientCode')} value={c.noClientCode} />
          <TrivecSummaryChip label={t('adminTesting.vangelderReplay.countErrors')} value={c.errors} />
        </div>
      </div>

      {result.emails.length === 0 ? (
        <div className={cn(secondaryText, smallTextSize, 'text-center py-4')}>{t('adminTesting.vangelderReplay.noEmails')}</div>
      ) : (
        <div>
          <button
            onClick={() => setShowEmails((s) => !s)}
            className={cn('inline-flex items-center gap-1.5', secondaryText, 'text-xs', semiBoldText, 'hover:opacity-80')}
          >
            {showEmails ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
            {t('adminTesting.vangelderReplay.perEmailToggle', { count: result.emails.length })}
          </button>
          {showEmails && (
            <div className="mt-2 space-y-2">
              {result.emails.map((e) => (
                <div key={`${e.account}:${e.gmailMessageId}`} className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 space-y-1">
                  <div className="flex flex-wrap items-center gap-2 min-w-0">
                    <span className={cn(primaryText, 'text-xs', semiBoldText, 'tabular-nums')}>{e.emailDate ? e.emailDate.slice(0, 10) : '—'}</span>
                    <span className={cn(secondaryText, 'text-xs truncate')}>{e.subject}</span>
                  </div>
                  <div className={cn(secondaryText, 'text-xs tabular-nums')}>
                    {e.orderNumberText ?? '—'}{e.clientCode ? ` · ${e.clientCode}` : ''} · {e.account}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {e.skippedReason && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300">
                        {t(VANGELDER_SKIP_REASON_KEYS[e.skippedReason])}
                      </span>
                    )}
                    {e.targets.map((tg) => (
                      <span
                        key={tg.restaurantPlatformId}
                        className={cn('px-2 py-0.5 rounded-full text-xs font-semibold',
                          tg.prediction === 'unresolvable' || tg.prediction === 'unknown'
                            ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300'
                            : 'bg-gray-100 dark:bg-gray-700/60 text-gray-600 dark:text-gray-300')}
                      >
                        {tg.label ? `${tg.label} · ` : ''}{t(VANGELDER_PREDICTION_KEYS[tg.prediction])}
                        {tg.storedAfter !== undefined && ` · ${tg.storedAfter ? t('adminTesting.vangelderReplay.storedAfterYes') : t('adminTesting.vangelderReplay.storedAfterNo')}`}
                      </span>
                    ))}
                    {e.error && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300">
                        {e.error}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
